import React, { Fragment, useContext } from "react"
import { MdClose } from "react-icons/md"
import { applyModalitySpecificDefaults } from "../../../../../Constants/LabelSettings"
import {
	MobergButton,
	MobergButtonShape,
	MobergButtonVariant,
	MobergCheckbox,
	MobergDropdown,
	MobergIconSize,
	MobergInputLabel,
	MobergNumberInput,
	MobergRow,
	MobergShortTextInput,
	MobergTheme,
} from "../../../../../Moberg"
import { ColorSpectrum } from "../../../../../Pages/Data/Visualize/DataReview/Types/ColorSpectrum"
import {
	DEFAULT_QEEG_DSA_CHANNEL_GROUP_ID,
	getQEEGDSATraceName,
	QEEG_DSA_CHANNEL_GROUPS,
	QEEGDSAChannelGroupId,
} from "../../../../../Pages/Data/Visualize/DataReview/Types/AnalysisDetails"
import {
	DEFAULT_DSA_DISPLAY_SCALE,
	DSA_FREQUENCY_LABELS,
	DSADisplayScale,
	getSpectrogramDataKey,
	isQEEGDSAAnalysis,
	RenderStrategy,
	SpectrogramTraceConfigJSON,
	TraceConfigJSON,
	TraceOption,
} from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { ConfigureWindowModalContext } from "../ConfigureWindowModal"
import { SpectrumSample } from "../SpectrumSample"
import { SpectrogramGraphModalitiesContext } from "./SpectrogramGraphModalities"

const DISPLAY_SCALE_OPTIONS: Array<{ label: string; value: DSADisplayScale }> = [
	{ label: "Power (µV²)", value: "power" },
	{ label: "Voltage (µV)", value: "voltage" },
	{ label: "SQRT Voltage (√µV)", value: "sqrt_voltage" },
]

const CHANNEL_GROUP_OPTIONS: Array<{ label: string; value: QEEGDSAChannelGroupId }> = QEEG_DSA_CHANNEL_GROUPS.map(channelGroup => ({
	label: channelGroup.name,
	value: channelGroup.id,
}))

const getDefaultDSASpectrogramSettings = (
	channelGroupId: QEEGDSAChannelGroupId
): Pick<SpectrogramTraceConfigJSON, "name" | "displayScale" | "colorSpectrum" | "lowerBound" | "upperBound" | "frequencyLabels"> => {
	const defaults = applyModalitySpecificDefaults({}, "qEEG_DSA", RenderStrategy.SPECTROGRAM)

	return {
		name: getQEEGDSATraceName(channelGroupId),
		displayScale: defaults.displayScale ?? DEFAULT_DSA_DISPLAY_SCALE,
		colorSpectrum: defaults.colorSpectrum ?? ColorSpectrum.DISCRETE_RGB,
		lowerBound: defaults.lowerBound ?? 0,
		upperBound: defaults.upperBound ?? 2,
		frequencyLabels: DSA_FREQUENCY_LABELS,
	}
}

export const AnalyticSpectrogramModalities: React.FC = () => {
	const { traceOptions, updateGraphProperty } = useContext(ConfigureWindowModalContext)
	const { activeAnalyticSpectrogramTraces, handleDelete, handleColorSpectrumChange, handleValidationErrorChange, updateTrace } =
		useContext(SpectrogramGraphModalitiesContext)

	const MAX_SPECTROGRAM_TRACES = 10
	const spectrogramOptions = traceOptions.filter((option: TraceOption) =>
		option.value.onDemandAnalysis && option.supportedRenderStrategies.includes(RenderStrategy.SPECTROGRAM)
	)
	const reasonAddTraceIsDisabled = activeAnalyticSpectrogramTraces.length >= MAX_SPECTROGRAM_TRACES
		? `There cannot be more than ${MAX_SPECTROGRAM_TRACES} spectrogram traces in a graph.`
		: spectrogramOptions.length === 0
		? "No spectrogram analytics available."
		: undefined

	const getTraceChannelGroupId = (trace: TraceOption["value"]): QEEGDSAChannelGroupId => {
		return isQEEGDSAAnalysis(trace.onDemandAnalysis)
			? trace.onDemandAnalysis.channelGroupId
			: DEFAULT_QEEG_DSA_CHANNEL_GROUP_ID
	}

	const buildSpectrogramTraceBase = (
		baseTrace: TraceOption["value"],
		displayScale: DSADisplayScale,
		channelGroupId?: QEEGDSAChannelGroupId
	): TraceOption["value"] => {
		const onDemandAnalysis = isQEEGDSAAnalysis(baseTrace.onDemandAnalysis)
			? {
				...baseTrace.onDemandAnalysis,
				channelGroupId: channelGroupId ?? baseTrace.onDemandAnalysis.channelGroupId,
				displayScale,
			}
			: baseTrace.onDemandAnalysis

		return {
			...baseTrace,
			onDemandAnalysis,
			dataKey: getSpectrogramDataKey(baseTrace.rawDataKey, onDemandAnalysis),
		}
	}

	const handleAddTrace = () => {
		const defaultOption = spectrogramOptions[0]

		if (!defaultOption) {
			return
		}

		const displayScale = DEFAULT_DSA_DISPLAY_SCALE
		const channelGroupId = getTraceChannelGroupId(defaultOption.value)
		const baseTrace = buildSpectrogramTraceBase(defaultOption.value, displayScale, channelGroupId)
		const newTrace: SpectrogramTraceConfigJSON = {
			...baseTrace,
			...getDefaultDSASpectrogramSettings(channelGroupId),
			id: `trace-${new Date(Date.now()).toISOString()}`,
			renderStrategy: RenderStrategy.SPECTROGRAM,
			isVisible: true,
		}

		updateGraphProperty("traces", (previous: TraceConfigJSON[] = []) => [
			...previous.filter(trace => trace.renderStrategy !== RenderStrategy.DELTA),
			newTrace,
		])
		updateTrace(newTrace.id, newTrace)
	}

	const handleAnalyticChange = (traceId: string, newBase: TraceOption["value"]) => {
		const existingTrace = activeAnalyticSpectrogramTraces.find(t => t.id === traceId)
		if (existingTrace) {
			const displayScale = DEFAULT_DSA_DISPLAY_SCALE
			const channelGroupId = getTraceChannelGroupId(newBase)
			const baseTrace = buildSpectrogramTraceBase(newBase, displayScale, channelGroupId)
			updateTrace(traceId, {
				...baseTrace,
				...getDefaultDSASpectrogramSettings(channelGroupId),
			})
		}
	}

	const handleChannelGroupChange = (traceId: string, channelGroupId: QEEGDSAChannelGroupId) => {
		const existingTrace = activeAnalyticSpectrogramTraces.find(t => t.id === traceId)
		if (!existingTrace || !isQEEGDSAAnalysis(existingTrace.onDemandAnalysis)) {
			return
		}

		const defaults = getDefaultDSASpectrogramSettings(channelGroupId)
		updateTrace(traceId, {
			...buildSpectrogramTraceBase(existingTrace, defaults.displayScale, channelGroupId),
			...defaults,
		})
	}

	const handleDisplayScaleChange = (traceId: string, displayScale: DSADisplayScale) => {
		const existingTrace = activeAnalyticSpectrogramTraces.find(t => t.id === traceId)
		if (!existingTrace) {
			return
		}

		updateTrace(traceId, {
			...buildSpectrogramTraceBase(existingTrace, displayScale),
			displayScale,
		})
	}

	const handleBoundChange = (traceId: string, bound: "lowerBound" | "upperBound", value: number | null) => {
		updateTrace(traceId, { [bound]: value ?? 0 })
	}

	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: "180px 180px 150px 180px 180px 120px 120px 120px 80px 48px",
				columnGap: "16px",
				rowGap: "8px",
				padding: "0 16px",
				alignItems: "center",
				maxWidth: "fit-content",
			}}
		>
			<MobergInputLabel text="Analytic" />
			<MobergInputLabel text="Channel group" />
			<MobergInputLabel text="Label" />
			<MobergInputLabel text="Display scale" />
			<MobergInputLabel text="Color spectrum" />
			<MobergInputLabel text="Lower bound" />
			<MobergInputLabel text="" />
			<MobergInputLabel text="Upper bound" />
			<MobergInputLabel text="Visible" />
			<MobergInputLabel text="" />

			{activeAnalyticSpectrogramTraces.map(trace => (
				<Fragment key={trace.id}>
					<MobergDropdown
						options={spectrogramOptions}
						selectedValue={spectrogramOptions.find(option => option.value.rawDataKey === trace.rawDataKey)?.value ?? spectrogramOptions[0]?.value}
						onChange={newBase => handleAnalyticChange(trace.id, newBase)}
						equals={(a, b) => a.rawDataKey === b.rawDataKey && a.dataSource === b.dataSource}
						width={175}
					/>

					<MobergDropdown
						options={CHANNEL_GROUP_OPTIONS}
						selectedValue={getTraceChannelGroupId(trace)}
						onChange={channelGroupId => handleChannelGroupChange(trace.id, channelGroupId)}
						width={175}
					/>

					<MobergShortTextInput
						value={trace.name}
						defaultValue={trace.name}
						onChange={name => updateTrace(trace.id, { name })}
						style={{ width: "140px" }}
						limit={24}
					/>

					<MobergDropdown
						options={DISPLAY_SCALE_OPTIONS}
						selectedValue={trace.displayScale}
						onChange={scale => handleDisplayScaleChange(trace.id, scale)}
						width={175}
					/>

					<MobergDropdown
						selectedValue={trace.colorSpectrum}
						onChange={spectrum => handleColorSpectrumChange(spectrum, trace)}
						options={[
							{ label: "Inferno", value: ColorSpectrum.INFERNO },
							{ label: "Turbo", value: ColorSpectrum.TURBO },
							{ label: "Viridis", value: ColorSpectrum.VIRIDIS },
							{ label: "Grays", value: ColorSpectrum.GRAYS },
							{ label: "Greens", value: ColorSpectrum.GREENS },
							{ label: "Reds", value: ColorSpectrum.REDS },
							{ label: "Red-Blue", value: ColorSpectrum.RED_BLUE },
							{ label: "Red-Yellow-Green", value: ColorSpectrum.RED_YELLOW_GREEN },
							{ label: "RGB (Discrete)", value: ColorSpectrum.DISCRETE_RGB },
						]}
						width={175}
					/>

					<MobergNumberInput
						defaultValue={trace.lowerBound}
						onChange={value => handleBoundChange(trace.id, "lowerBound", value)}
						onValidationErrorChange={error => handleValidationErrorChange(`${trace.id}-lowerBound`, error)}
						style={{ width: "100%" }}
					/>

					<MobergRow>
						<SpectrumSample colorSpectrum={trace.colorSpectrum} />
					</MobergRow>

					<MobergNumberInput
						defaultValue={trace.upperBound}
						onChange={value => handleBoundChange(trace.id, "upperBound", value)}
						onValidationErrorChange={error => handleValidationErrorChange(`${trace.id}-upperBound`, error)}
						style={{ width: "100%" }}
					/>

					<MobergCheckbox isChecked={trace.isVisible ?? true} onChange={isVisible => updateTrace(trace.id, { isVisible })} />

					<MobergButton shape={MobergButtonShape.SQUARE} onClick={() => handleDelete(trace.id)} style={{ padding: "6px" }}>
						<MdClose size={MobergIconSize.REGULAR} />
					</MobergButton>
				</Fragment>
			))}

			<MobergButton
				onClick={handleAddTrace}
				theme={MobergTheme.BLUE}
				variant={MobergButtonVariant.OUTLINE}
				style={{ width: "175px" }}
				disabled={!!reasonAddTraceIsDisabled}
				tooltip={reasonAddTraceIsDisabled}
			>
				Add analytic
			</MobergButton>
		</div>
	)
}
